import { useEffect, useRef, useState } from 'react';
import * as DataListAPI from '../../../../api/dataList';
import api from '../../../../api';
import { pickSelectedId, printPdf } from '../../../../utils/funcoes';
import useFlashMessage from '../../../../hooks/useFlashMessage';

const vazio = { pessoaId: null, cargoId: null, pessoa: '', cargo: '', ordem: 1 };
export function useComissaoModule({ dados, setDados, bloqueado, dadosSalvos = {}, permissoes = {} }) {
    const [imprimindo, setImprimindo] = useState(false);
    const impressaoLock = useRef(false);
    const contextoImpressao = useRef(0);
    useEffect(() => {
        contextoImpressao.current += 1;
        return () => {
            contextoImpressao.current += 1;
        };
    }, [dadosSalvos.uuid]);
    const [item, setItem] = useState(vazio);
    const [editando, setEditando] = useState(null);
    const [pessoas, setPessoas] = useState([]);
    const [cargos, setCargos] = useState([]);
    const versao = useRef({ pessoa: 0, cargo: 0 });
    const { setFlashMessage } = useFlashMessage();
    useEffect(() => {
        setItem(vazio);
        setEditando(null);
        setPessoas([]);
        setCargos([]);
        versao.current.pessoa += 1;
        versao.current.cargo += 1;
        return () => {
            versao.current.pessoa += 1;
            versao.current.cargo += 1;
        };
    }, [dados.empresaId]);
    const consultar = async (campo, texto) => {
        const atual = ++versao.current[campo];
        if (!dados.empresaId || texto.trim().length < 2) {
            (campo === 'pessoa' ? setPessoas : setCargos)([]);
            return;
        }
        try {
            const result = await DataListAPI.dataList({
                empresaId: dados.empresaId,
                tabela: campo === 'pessoa' ? 'Pessoa' : 'rhCargo',
                condicao: 'eleicaoComissao',
                filtro: texto,
            });
            if (atual !== versao.current[campo]) return;
            if (!Array.isArray(result)) throw new Error('Não foi possível consultar os registros.');
            const lista = result.map((opcao) => ({ ...opcao, label: campo === 'pessoa' ? opcao.label || '' : '' }));
            (campo === 'pessoa' ? setPessoas : setCargos)(lista);
        } catch (e) {
            if (atual === versao.current[campo]) setFlashMessage(e.message, 'error');
        }
    };
    const alterar = ({ target: { name, value } }) => {
        if (bloqueado) return;
        if (name === 'ordem') {
            setItem((prev) => ({ ...prev, ordem: value }));
            return;
        }
        const opcoes = name === 'pessoa' ? pessoas : cargos;
        const id = pickSelectedId(value, opcoes);
        setItem((prev) => ({ ...prev, [name]: value, [`${name}Id`]: id }));
        if (!id) consultar(name, value);
    };
    const limpar = () => {
        setItem({ ...vazio, ordem: dados.comissao.length + 1 });
        setEditando(null);
        versao.current.pessoa += 1;
        versao.current.cargo += 1;
    };
    const adicionar = () => {
        if (bloqueado) return;
        if (!item.pessoaId || !item.cargoId || !Number.isInteger(Number(item.ordem)) || Number(item.ordem) < 1) {
            setFlashMessage('Selecione pessoa, cargo e uma ordem válida.', 'warning');
            return;
        }
        if (dados.comissao.some((m) => Number(m.pessoaId) === Number(item.pessoaId) && Number(m.pessoaId) !== editando)) {
            setFlashMessage('Essa pessoa já está na comissão.', 'warning');
            return;
        }
        const novo = {
            pessoaId: Number(item.pessoaId),
            cargoId: Number(item.cargoId),
            ordem: Number(item.ordem),
            Pessoa: { descricao: item.pessoa, label: pessoas.find((p) => Number(p.id) === Number(item.pessoaId))?.label || '' },
            rhCargo: { descricao: item.cargo },
        };
        setDados((prev) => ({
            ...prev,
            comissao:
                editando == null
                    ? [...prev.comissao, novo]
                    : prev.comissao.map((m) => (Number(m.pessoaId) === editando ? { ...m, ...novo } : m)),
        }));
        limpar();
    };
    const editar = (m) => {
        if (bloqueado) return;
        versao.current.pessoa += 1;
        versao.current.cargo += 1;
        const pessoa = m.Pessoa?.descricao || '',
            cargo = m.rhCargo?.descricao || '';
        setPessoas([{ id: m.pessoaId, descricao: pessoa, label: m.Pessoa?.label || '' }]);
        setCargos([{ id: m.cargoId, descricao: cargo, label: '' }]);
        setItem({ pessoaId: m.pessoaId, cargoId: m.cargoId, pessoa, cargo, ordem: m.ordem });
        setEditando(Number(m.pessoaId));
    };
    const remover = (m) => {
        if (bloqueado) return;
        setDados((prev) => ({ ...prev, comissao: prev.comissao.filter((v) => Number(v.pessoaId) !== Number(m.pessoaId)) }));
        if (editando === Number(m.pessoaId)) limpar();
    };
    const podeImprimir = Boolean(dadosSalvos.uuid && permissoes.autorizado && permissoes.imprimir);
    const comparar = (d) =>
        JSON.stringify({
            descricao: d.descricao || '',
            dataEleicao: d.dataEleicao || '',
            comissao: (d.comissao || [])
                .map((m) => [Number(m.pessoaId), Number(m.cargoId), Number(m.ordem)])
                .sort((a, b) => a[0] - b[0]),
        });
    const imprimirQualificacao = async () => {
        if (!podeImprimir || impressaoLock.current) return;
        if (editando != null || item.pessoa || item.cargo || comparar(dados) !== comparar(dadosSalvos)) {
            setFlashMessage(
                'Salve a eleição e conclua ou descarte a edição de membro antes de imprimir a qualificação.',
                'warning'
            );
            return;
        }
        const contexto = contextoImpressao.current;
        impressaoLock.current = true;
        setImprimindo(true);
        try {
            const { data } = await api.post(
                `/eleicao/${dadosSalvos.uuid}/comissao/qualificacao`,
                {},
                { responseType: 'arraybuffer' }
            );
            if (contexto === contextoImpressao.current) printPdf(data);
        } catch (e) {
            if (contexto !== contextoImpressao.current) return;
            let mensagem = e.response?.data?.message || e.message || 'Erro ao gerar a qualificação da comissão.';
            if (e.response?.data instanceof ArrayBuffer || ArrayBuffer.isView(e.response?.data)) {
                try {
                    mensagem = JSON.parse(new TextDecoder().decode(e.response.data)).message || mensagem;
                } catch {
                    /* mantém mensagem */
                }
            }
            setFlashMessage(mensagem, 'error');
        } finally {
            impressaoLock.current = false;
            setImprimindo(false);
        }
    };
    return {
        imprimindo,
        podeImprimir,
        imprimirQualificacao,
        item,
        pessoas,
        cargos,
        editando: editando != null,
        pendente: editando != null || Boolean(item.pessoa || item.cargo),
        alterar,
        adicionar,
        limpar,
        editar,
        remover,
    };
}
